/**
 * Children list page at `/children`.
 *
 * Shows every child in the caller's family, sorted by display_order,
 * with a thumbnail from IndexedDB, birthday summary, inline Edit /
 * Delete / move controls, and an "Add child" CTA.
 *
 * Reorder UX: up/down arrow buttons next to each row. A future polish
 * pass (Phase 7) could swap this for dnd-kit drag-and-drop — the
 * backend reorder contract accepts the full list either way. Arrow
 * buttons are chosen now because they:
 *   1. Need zero new dependencies
 *   2. Work on touch + keyboard + screen readers out of the box
 *   3. Match the "reflective, not playful" motion rule — no drag
 *      overshoot animations to tune.
 *
 * Delete confirmation has specific copy explaining that the photo
 * cached on this device will also be removed. That's the user's
 * only chance to cancel before we drop the IndexedDB entry.
 */

import { useState, type ReactNode } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { ApiError } from "@/lib/api";
import { deleteChildPhoto } from "@/lib/photos";
import { usePhotoURL } from "@/lib/usePhotoURL";
import { BrandMark } from "@/components/BrandMark";
import { Wordmark } from "@/components/Wordmark";
import { LoadingShell } from "@/components/LoadingShell";
import { ChildForm } from "@/features/children/ChildForm";
import {
  useChildren,
  useDeleteChild,
  useReorderChildren,
  type Child,
} from "@/features/children/useChildren";

type PageMode =
  | { kind: "list" }
  | { kind: "add" }
  | { kind: "edit"; child: Child };

export function ChildrenListPage() {
  const [mode, setMode] = useState<PageMode>({ kind: "list" });
  const { data: children, isLoading, error } = useChildren();
  const reorder = useReorderChildren();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (children === undefined || over === null || active.id === over.id) return;
    const oldIndex = children.findIndex((c) => c.id === active.id);
    const newIndex = children.findIndex((c) => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = children.map((c) => c.id);
    newOrder.splice(oldIndex, 1);
    const moved = children[oldIndex];
    if (moved === undefined) return;
    newOrder.splice(newIndex, 0, moved.id);
    reorder.mutate({ child_ids: newOrder });
  };

  if (isLoading) {
    return <LoadingShell message="Loading children…" />;
  }

  if (error !== null && error !== undefined) {
    return (
      <PageFrame eyebrow="Children">
        <h1 className="font-serif text-display-md text-ink">
          Couldn't load your children.
        </h1>
        <p className="text-[15px] text-ink-muted leading-[1.6] max-w-[560px]">
          {error instanceof ApiError ? error.detail : "Try refreshing."}
        </p>
      </PageFrame>
    );
  }

  if (mode.kind === "add") {
    return (
      <PageFrame eyebrow="Add a child">
        <h1 className="font-serif text-display-md text-ink">
          Tell us about <em className="italic font-normal text-coral-deep">them</em>.
        </h1>
        <ChildForm
          onSaved={() => setMode({ kind: "list" })}
          onCancel={() => setMode({ kind: "list" })}
        />
      </PageFrame>
    );
  }

  if (mode.kind === "edit") {
    return (
      <PageFrame eyebrow={`Edit ${mode.child.name}`}>
        <h1 className="font-serif text-display-md text-ink">
          Update <em className="italic font-normal text-coral-deep">{mode.child.name}</em>
        </h1>
        <ChildForm
          initial={mode.child}
          onSaved={() => setMode({ kind: "list" })}
          onCancel={() => setMode({ kind: "list" })}
        />
      </PageFrame>
    );
  }

  const rows = children ?? [];

  return (
    <PageFrame eyebrow="Your children">
      <div className="flex items-start justify-between gap-4">
        <h1 className="font-serif text-display-md text-ink">
          {rows.length === 0 ? (
            <>
              No children <em className="italic font-normal text-coral-deep">yet</em>.
            </>
          ) : (
            <>
              Your <em className="italic font-normal text-coral-deep">family</em>
            </>
          )}
        </h1>
        <button
          type="button"
          onClick={() => setMode({ kind: "add" })}
          className="shrink-0 inline-flex items-center px-5 py-2 rounded-md font-sans font-semibold text-white shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
          style={{
            background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
            boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
          }}
        >
          Add child
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="text-[15px] text-ink-muted leading-[1.6] max-w-[560px]">
          Add your first child to see your Perspectives — 18 Summers, Santa
          Years, Family Dinners Until 18, and more. Perspectives calculate
          automatically from the child's birthday.
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={rows.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="flex flex-col gap-3">
              {rows.map((child) => (
                <SortableChildRow
                  key={child.id}
                  child={child}
                  onEdit={() => setMode({ kind: "edit", child })}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface SortableChildRowProps {
  child: Child;
  onEdit: () => void;
}

function SortableChildRow({ child, onEdit }: SortableChildRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: child.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const { url: photoUrl } = usePhotoURL(child.id);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteChild = useDeleteChild();

  const handleDelete = async () => {
    setDeleteError(null);
    try {
      await deleteChild.mutateAsync({ id: child.id });
      await deleteChildPhoto(child.id);
      setConfirmingDelete(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setDeleteError(err.detail);
      } else {
        setDeleteError("Couldn't delete. Try again?");
      }
    }
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex flex-col gap-3 rounded-md bg-surface border border-edge px-4 md:px-5 py-4"
    >
      <div className="flex items-start gap-3 md:gap-4">
        <button
          type="button"
          className="w-10 h-14 md:h-16 shrink-0 flex items-center justify-center text-ink-dim hover:text-ink-muted cursor-grab active:cursor-grabbing touch-none"
          aria-label={`Drag to reorder ${child.name}`}
          {...attributes}
          {...listeners}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <circle cx="5" cy="3" r="1.5" />
            <circle cx="11" cy="3" r="1.5" />
            <circle cx="5" cy="8" r="1.5" />
            <circle cx="11" cy="8" r="1.5" />
            <circle cx="5" cy="13" r="1.5" />
            <circle cx="11" cy="13" r="1.5" />
          </svg>
        </button>

        <div className="w-14 h-14 md:w-16 md:h-16 rounded-md bg-surface border border-edge overflow-hidden flex items-center justify-center shrink-0">
          {photoUrl === null ? (
            <ChildSilhouette />
          ) : (
            // eslint-disable-next-line jsx-a11y/alt-text
            <img src={photoUrl} alt="" className="w-full h-full object-cover" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-serif text-title text-ink">{child.name}</div>
          <div className="font-sans text-[13px] text-ink-muted mt-1">
            {formatBirthday(child)}
          </div>
          {child.notes !== null && child.notes.length > 0 && (
            <div className="font-sans text-[13px] text-ink-soft mt-2 line-clamp-2">
              {child.notes}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onEdit}
          className="px-4 py-1.5 rounded-md font-sans text-[13px] font-medium text-ink bg-surface border border-edge hover:border-coral-deep hover:text-coral-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => setConfirmingDelete(true)}
          className="px-4 py-1.5 rounded-md font-sans text-[13px] text-ink-muted hover:text-coral-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
        >
          Delete
        </button>
      </div>

      {confirmingDelete && (
        <div className="rounded-md border border-coral-deep/30 bg-coral-soft/30 px-4 py-3 flex flex-col gap-3">
          <div className="font-sans text-[13px] text-ink-soft">
            Remove <span className="font-semibold text-ink">{child.name}</span>?
            The photo of {child.name} stored on this device will also be removed.
            This can't be undone.
          </div>
          {deleteError !== null && (
            <div role="alert" className="font-sans text-[13px] text-coral-deep">
              {deleteError}
            </div>
          )}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleteChild.isPending}
              className="px-4 py-2 rounded-md font-sans text-[13px] font-semibold text-white bg-coral-deep disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {deleteChild.isPending ? "Removing…" : "Yes, remove"}
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirmingDelete(false);
                setDeleteError(null);
              }}
              disabled={deleteChild.isPending}
              className="px-4 py-2 rounded-md font-sans text-[13px] text-ink-muted hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBirthday(child: Child): string {
  const { birth_year, birth_month, birth_day } = child;
  if (birth_month === null) {
    return `Born ${birth_year}`;
  }
  const monthName = MONTH_NAMES[birth_month - 1] ?? String(birth_month);
  if (birth_day === null) {
    return `Born ${monthName} ${birth_year}`;
  }
  return `Born ${monthName} ${birth_day}, ${birth_year}`;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

function ChildSilhouette() {
  return (
    <svg
      viewBox="0 0 64 64"
      className="w-10 h-10 text-ink-muted/50"
      aria-hidden="true"
    >
      <circle cx="32" cy="24" r="11" fill="currentColor" />
      <path d="M10 56c0-12.15 9.85-22 22-22s22 9.85 22 22" fill="currentColor" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Page frame (matches CreateFamilyPage / AcceptInvitationPage structure)
// ---------------------------------------------------------------------------

interface PageFrameProps {
  eyebrow: string;
  children: ReactNode;
}

function PageFrame({ eyebrow, children }: PageFrameProps) {
  return (
    <main className="max-w-[720px] mx-auto px-6 md:px-11 pt-24 pb-16 flex flex-col gap-8">
      <div className="flex items-center gap-4">
        <BrandMark />
        <Wordmark size="hero" />
      </div>
      <div className="font-sans text-eyebrow uppercase text-coral-deep font-semibold">
        {eyebrow}
      </div>
      {children}
    </main>
  );
}
